<template>
  <div class="condicional-create-page">
    <div class="d-flex align-items-center justify-content-between mb-5">
      <div>
        <h3 class="mb-1">{{ tituloPagina }}</h3>
        <span class="text-muted">
          {{ subtituloPagina }}
        </span>
      </div>

      <button
        type="button"
        class="btn btn-light font-weight-bolder"
        @click="$router.push({ name: 'condicionalMercadorias' })"
      >
        Voltar
      </button>
    </div>

    <div class="card mb-5">
      <div class="card-body">
        <b-row>
          <b-col md="3">
            <b-form-group label="Cliente" label-for="cliente">
              <b-form-input
                v-if="form.responsabilidade_vendedor"
                id="cliente"
                value="Responsabilidade do vendedor"
                readonly
              />
              <VueSelect
                v-else
                id="cliente"
                v-model="form.cliente"
                :options="clientes"
                :filterable="false"
                label="nome"
                class="busca-cliente"
                placeholder="Informe o nome do cliente"
                @search="buscarClientes"
              >
                <template #option="option">
                  <div>
                    <strong>{{ option.nome }}</strong>
                    <div class="text-muted small">
                      {{
                        option.cpf_cnpj || option.cnpj_cpf
                          ? formatCpfCnpj(option.cpf_cnpj || option.cnpj_cpf)
                          : "-"
                      }}
                    </div>
                  </div>
                </template>
                <template #selected-option="option">
                  {{ option.nome }}
                </template>
                <template #no-options>Informe o nome do cliente</template>
              </VueSelect>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group
              label="Responsabilidade do vendedor"
              label-for="responsabilidade_vendedor"
            >
              <b-form-checkbox
                id="responsabilidade_vendedor"
                v-model="form.responsabilidade_vendedor"
                class="condicional-responsabilidade-flag"
                switch
                @change="alterarResponsabilidadeVendedor"
              >
                Sim
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group label="Vendedor" label-for="vendedor">
              <VueSelect
                id="vendedor"
                v-model="form.vendedor"
                :options="vendedores"
                :filterable="false"
                label="nome"
                placeholder="Informe o vendedor"
                @open="buscarVendedores"
                @search="buscarVendedores"
              >
                <template #no-options>Informe o nome do vendedor</template>
              </VueSelect>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Data de saída" label-for="data_saida">
              <b-form-input
                id="data_saida"
                v-model="form.data_saida"
                type="date"
              />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group
              label="Limite de retorno"
              label-for="data_limite_retorno"
            >
              <b-form-input
                id="data_limite_retorno"
                v-model="form.data_limite_retorno"
                type="date"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Observação" label-for="observacao" class="mb-0">
          <b-form-textarea
            id="observacao"
            v-model="form.observacao"
            rows="2"
            max-rows="4"
          />
        </b-form-group>
      </div>
    </div>

    <div class="card mb-5">
      <div class="card-body">
        <b-row align-v="end">
          <b-col md="5">
            <b-form-group label="Produto" label-for="produto">
              <VueSelect
                id="produto"
                ref="produtoSelect"
                v-model="produtoSelecionado"
                :options="produtos"
                :filterable="false"
                label="descricao"
                placeholder="Busque por nome, código ou código de barras"
                @search="buscarProdutos"
                @input="selecionarProduto"
              >
                <template #option="option">
                  <div>
                    <strong>{{ option.descricao }}</strong>
                    <div class="text-muted small">
                      Código: {{ option.codigo || "-" }} · Referência:
                      {{ option.referencia || "-" }} · Estoque:
                      {{ formatQuantidade(estoqueDisponivel(option)) }} · Reservado:
                      {{ formatQuantidade(option.quantidade_reservada_condicional) }}
                      <span v-if="combinacaoLabel(option)" class="ml-2">
                        · {{ combinacaoLabel(option) }}
                      </span>
                    </div>
                  </div>
                </template>
                <template #selected-option="option">
                  {{ option.descricao }}
                </template>
                <template #no-options>Informe ao menos 3 caracteres</template>
              </VueSelect>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Estoque disponível" label-for="estoque">
              <b-form-input
                id="estoque"
                :value="formatQuantidade(estoqueProdutoSelecionado)"
                readonly
              />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Quantidade" label-for="quantidade">
              <b-form-input
                id="quantidade"
                v-model.number="item.quantidade"
                type="number"
                min="0.0001"
                step="0.0001"
              />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Valor unitário" label-for="valor_unitario">
              <b-form-input
                id="valor_unitario"
                v-model.number="item.valor_unitario"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>

          <b-col md="1">
            <button
              type="button"
              class="btn btn-primary btn-block"
              v-b-tooltip.hover.bottom="'Adicionar item'"
              :disabled="carregandoEstoqueProduto"
              @click="adicionarItem"
            >
              +
            </button>
          </b-col>
        </b-row>

        <div class="table-responsive mt-4">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Produto</th>
                <th class="text-right">Estoque</th>
                <th class="text-right">Quantidade</th>
                <th class="text-right">Valor unitário</th>
                <th class="text-right">Total</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="form.itens.length === 0">
                <td colspan="6" class="text-center text-muted py-6">
                  Nenhum produto incluído.
                </td>
              </tr>
              <tr v-for="(itemLinha, index) in form.itens" :key="itemLinha.uid">
                <td>
                  <strong>{{ itemLinha.descricao }}</strong>
                  <div class="text-muted small">
                    Código: {{ itemLinha.codigo || "-" }}, Referência:
                    {{ itemLinha.referencia || "-" }}
                    <span v-if="combinacaoLabel(itemLinha)" class="ml-2">
                      · {{ combinacaoLabel(itemLinha) }}
                    </span>
                  </div>
                </td>
                <td class="text-right">
                  {{ formatQuantidade(itemLinha.estoque_disponivel) }}
                </td>
                <td class="text-right">
                  <b-form-input
                    v-model.number="itemLinha.quantidade"
                    class="text-right condicional-number-input"
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    @change="validarQuantidadeLinha(itemLinha)"
                  />
                </td>
                <td class="text-right">
                  <b-form-input
                    v-model.number="itemLinha.valor_unitario"
                    class="text-right condicional-number-input"
                    type="number"
                    min="0"
                    step="0.01"
                  />
                </td>
                <td class="text-right">
                  R$ {{ formatValor(totalItem(itemLinha)) }}
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="removerItem(index)"
                  >
                    Remover
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="2">Total</th>
                <th class="text-right">
                  {{ formatQuantidade(totalQuantidade) }}
                </th>
                <th></th>
                <th class="text-right">R$ {{ formatValor(totalGeral) }}</th>
                <th></th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <button
        type="button"
        class="btn btn-light-primary font-weight-bolder mr-3"
        :disabled="salvando || carregando"
        @click="salvar(false)"
      >
        {{ editando ? "Salvar alterações" : "Salvar rascunho" }}
      </button>
      <button
        v-if="podeConfirmarSaida"
        type="button"
        class="btn btn-primary font-weight-bolder"
        :disabled="salvando || carregando"
        @click="confirmarSalvarSaida"
      >
        Salvar e confirmar saída
      </button>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment";
import swal from "sweetalert";
import VueSelect from "vue-select";
import { mapState } from "vuex";
import { SistemaMixin } from "@/modules/Application";
import CondicionalMercadoriaData from "../CondicionalMercadoriaData";
import { store as StoreVenda } from "@/modules/Vendas/storeVenda";

export default {
  name: "condicionalMercadoriasNova",
  mixins: [SistemaMixin],
  components: {
    VueSelect,
  },
  data() {
    return {
      salvando: false,
      carregando: false,
      editando: false,
      idCondicionalMercadoria: null,
      statusCondicional: null,
      itensOriginaisEstoque: [],
      clientes: [],
      vendedores: [],
      produtos: [],
      produtoSelecionado: null,
      carregandoEstoqueProduto: false,
      form: {
        cliente: null,
        vendedor: null,
        responsabilidade_vendedor: false,
        data_saida: moment().format("YYYY-MM-DD"),
        data_limite_retorno: moment().add(7, "days").format("YYYY-MM-DD"),
        observacao: "",
        itens: [],
      },
      item: {
        quantidade: 1,
        valor_unitario: 0,
      },
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
    tituloPagina() {
      return this.editando
        ? "Editar Condicional de Mercadorias"
        : "Nova Condicional de Mercadorias";
    },
    subtituloPagina() {
      return this.editando
        ? "Altere os dados da condicional de mercadorias."
        : "Registre a saída temporária de mercadorias para o cliente.";
    },
    podeConfirmarSaida() {
      return !this.editando || Number(this.statusCondicional) === 1;
    },
    estoqueProdutoSelecionado() {
      return this.estoqueDisponivel(this.produtoSelecionado);
    },
    desconsiderarEstoqueCondicional() {
      return Boolean(StoreVenda?.desconsiderarEstoqueCondicional);
    },
    totalQuantidade() {
      return this.form.itens.reduce(
        (total, item) => total + this.toFloat(item.quantidade),
        0
      );
    },
    totalGeral() {
      return this.form.itens.reduce(
        (total, item) => total + this.totalItem(item),
        0
      );
    },
  },
  mounted() {
    this.$nextTick(() => this.registrarLeitorProduto());

    const idCondicional = this.$route.params.id;

    if (idCondicional) {
      this.carregarParaEdicao(idCondicional);
      return;
    }

    if (this.currentUser?.vendedor) {
      this.form.vendedor = this.currentUser.vendedor;
    }
  },
  beforeDestroy() {
    this.removerLeitorProduto();
  },
  methods: {
    criarEstadoLeitorProduto() {
      return {
        value: "",
        startedAt: 0,
        lastAt: 0,
        intervals: [],
        keyCount: 0,
      };
    },
    registrarLeitorProduto() {
      const input = this.$refs.produtoSelect?.$el?.querySelector("input");
      if (!input || input === this._produtoLeitorInput) return;

      this.removerLeitorProduto();
      this._produtoLeitorInput = input;
      this._produtoLeitorState = this.criarEstadoLeitorProduto();
      this._onProdutoLeitorKeydown = (event) =>
        this.registrarTeclaLeitorProduto(event);
      input.addEventListener("keydown", this._onProdutoLeitorKeydown);
    },
    removerLeitorProduto() {
      if (this._produtoLeitorInput && this._onProdutoLeitorKeydown) {
        this._produtoLeitorInput.removeEventListener(
          "keydown",
          this._onProdutoLeitorKeydown
        );
      }
      this._produtoLeitorInput = null;
      this._onProdutoLeitorKeydown = null;
      this._produtoLeitorState = this.criarEstadoLeitorProduto();
    },
    registrarTeclaLeitorProduto(event) {
      const key = event?.key || "";

      if (event?.ctrlKey || event?.metaKey || event?.altKey) return;

      if (["Backspace", "Delete", "Escape"].includes(key)) {
        this._produtoLeitorState = this.criarEstadoLeitorProduto();
        return;
      }

      if (key === "Enter") return;

      if (!/^\d$/.test(key)) {
        this._produtoLeitorState = this.criarEstadoLeitorProduto();
        return;
      }

      const now = Date.now();
      const state = this._produtoLeitorState || this.criarEstadoLeitorProduto();
      const tempoDesdeUltima = state.lastAt ? now - state.lastAt : 0;

      if (!state.startedAt || tempoDesdeUltima > 250) {
        this._produtoLeitorState = {
          value: key,
          startedAt: now,
          lastAt: now,
          intervals: [],
          keyCount: 1,
        };
        return;
      }

      state.value += key;
      state.intervals.push(tempoDesdeUltima);
      state.lastAt = now;
      state.keyCount += 1;
      this._produtoLeitorState = state;
    },
    entradaProdutoFoiLeitorReferencia(busca) {
      const termo = String(busca || "").trim();
      if (!/^\d{2,}$/.test(termo)) return false;

      const state = this._produtoLeitorState;
      if (!state || state.value !== termo || state.keyCount !== termo.length) {
        return false;
      }

      if (!state.intervals.length || Date.now() - state.lastAt > 1000) {
        return false;
      }

      const totalIntervalos = state.intervals.reduce(
        (acc, intervalo) => acc + intervalo,
        0
      );
      const mediaIntervalos = totalIntervalos / state.intervals.length;
      const maiorIntervalo = Math.max(...state.intervals);

      return mediaIntervalos <= 60 && maiorIntervalo <= 90;
    },
    produtoComReferenciaExataUnica(produtos, busca) {
      const termo = String(busca || "").trim();
      const produtosComReferencia = (produtos || []).filter(
        (produto) => String(produto?.referencia ?? "").trim() === termo
      );

      return produtosComReferencia.length === 1
        ? produtosComReferencia[0]
        : null;
    },
    async carregarParaEdicao(idCondicional) {
      this.carregando = true;

      try {
        const condicional = await CondicionalMercadoriaData.find(
          this,
          idCondicional
        );

        if (![1, 2].includes(Number(condicional.status))) {
          this.$toast.warning(
            "Apenas condicionais em Rascunho ou Em aberto podem ser editadas."
          );
          this.$router.push({ name: "condicionalMercadorias" });
          return;
        }

        this.editando = true;
        this.idCondicionalMercadoria =
          condicional.id_condicional_mercadoria || Number(idCondicional);
        this.statusCondicional = Number(condicional.status);
        this.preencherFormulario(condicional);
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            error?.message ||
            "Erro ao carregar condicional."
        );
        this.$router.push({ name: "condicionalMercadorias" });
      } finally {
        this.carregando = false;
      }
    },
    preencherFormulario(condicional) {
      this.form.responsabilidade_vendedor = Boolean(
        condicional.responsabilidade_vendedor
      );
      this.form.cliente = this.montarClienteEdicao(condicional);
      this.form.vendedor = this.montarVendedorEdicao(condicional);
      this.form.data_saida = condicional.data_saida
        ? moment(condicional.data_saida).format("YYYY-MM-DD")
        : "";
      this.form.data_limite_retorno = condicional.data_limite_retorno
        ? moment(condicional.data_limite_retorno).format("YYYY-MM-DD")
        : "";
      this.form.observacao = condicional.observacao || "";
      this.form.itens = (condicional.itens || []).map((item) =>
        this.mapearItemEdicao(item)
      );
      this.itensOriginaisEstoque = this.assinaturaItensEstoque(this.form.itens);
    },
    montarClienteEdicao(condicional) {
      if (condicional.responsabilidade_vendedor) {
        return null;
      }

      const cliente = condicional.cliente;

      if (cliente && typeof cliente === "object") {
        return {
          ...cliente,
          id_pessoa: cliente.id_pessoa ?? condicional.id_pessoa,
          nome: cliente.nome,
        };
      }

      if (condicional.id_pessoa) {
        return {
          id_pessoa: condicional.id_pessoa,
          nome: typeof cliente === "string" ? cliente : condicional.cliente_nome,
        };
      }

      return null;
    },
    montarVendedorEdicao(condicional) {
      const vendedor = condicional.vendedor;

      if (vendedor && typeof vendedor === "object") {
        return {
          ...vendedor,
          id_vendedor: vendedor.id_vendedor ?? condicional.id_vendedor,
          nome: vendedor.nome,
        };
      }

      if (condicional.id_vendedor) {
        return {
          id_vendedor: condicional.id_vendedor,
          nome: typeof vendedor === "string" ? vendedor : null,
        };
      }

      return null;
    },
    mapearItemEdicao(item) {
      const combinacao = item.combinacao || null;

      return {
        uid: `${item.id_condicional_mercadoria_item || item.id_produto}-${Date.now()}-${Math.random()}`,
        id_condicional_mercadoria_item:
          item.id_condicional_mercadoria_item || null,
        id_produto: item.id_produto,
        id_produto_lote: item.id_produto_lote || null,
        combinacao,
        variavel: Boolean(item.variavel || combinacao),
        descricao:
          item.descricao_produto_snapshot || item.produto?.descricao || "-",
        codigo: item.codigo_produto_snapshot || item.produto?.codigo || null,
        referencia: item.produto?.referencia || null,
        estoque_disponivel: null,
        quantidade: this.toFloat(item.quantidade_enviada),
        valor_unitario: this.toFloat(item.valor_unitario),
      };
    },
    alterarResponsabilidadeVendedor() {
      if (this.form.responsabilidade_vendedor) {
        this.form.cliente = null;
      }
    },
    buscarClientes(search, loading) {
      if (!search || search.length < 2) return;

      loading(true);
      this.buscarClientesDebounced(search, loading, this);
    },
    buscarClientesDebounced: debounce(async (search, loading, vm) => {
      vm.clientes = await vm.getClientes(search);
      loading(false);
    }, 350),
    buscarVendedores(search, loading) {
      const busca = this.prepararBuscaDireta(search, loading);
      busca.loading(true);
      this.buscarVendedoresDebounced(busca.termo, busca.loading, this);
    },
    buscarVendedoresDebounced: debounce(async (search, loading, vm) => {
      vm.vendedores = await vm.getVendedorByNome(search);
      loading(false);
    }, 350),
    buscarProdutos(search, loading) {
      if (!search) return;

      loading(true);
      this.buscarProdutosDebounced(search, loading, this);
    },
    buscarProdutosDebounced: debounce(async (search, loading, vm) => {
      try {
        const { data } = await vm.$http.post("/produtos/filter-item", {
          filter: search,
          typeFilter: 1,
          nfce: true,
          apresentar_variacoes: true,
          considerar_somente_qtd: false,
        });

        vm.produtos = data || [];
        const adicionouPorCodigoBarras =
          await vm.adicionarProdutoPorCodigoBarras(search);

        if (
          !adicionouPorCodigoBarras &&
          vm.entradaProdutoFoiLeitorReferencia(search)
        ) {
          const produtoReferencia = vm.produtoComReferenciaExataUnica(
            data,
            search
          );

          if (produtoReferencia) {
            await vm.selecionarProduto(produtoReferencia, true);
          }
        }
      } catch (error) {
        vm.produtos = [];
      } finally {
        loading(false);
      }
    }, 350),
    async selecionarProduto(produto, adicionarAutomaticamente = false) {
      if (!produto) return;

      this.carregandoEstoqueProduto = true;

      try {
        const estoque = await CondicionalMercadoriaData.estoqueDisponivel(
          this,
          {
            id_produto: produto.id,
            id_produto_lote: produto.lote_baixa?.id_produto_lote || null,
            combinacao: produto.combinacao || null,
          }
        );

        this.produtoSelecionado = {
          ...produto,
          estoque_disponivel: estoque.estoque_disponivel,
        };
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            error?.message ||
            "Erro ao consultar estoque disponível."
        );
        this.produtoSelecionado = null;
      } finally {
        this.carregandoEstoqueProduto = false;
      }

      if (!this.produtoSelecionado) return;

      this.item.valor_unitario = this.toFloat(
        this.produtoSelecionado.preco_lista_cliente ||
          this.produtoSelecionado.preco ||
          0
      );
      this.item.quantidade = 1;

      if (adicionarAutomaticamente) {
        this.adicionarItem();
      }
    },
    async adicionarProdutoPorCodigoBarras(search) {
      const codigoBarras = this.normalizarCodigoBarras(search);

      if (
        !codigoBarras ||
        codigoBarras.length < 6 ||
        this.produtos.length !== 1
      ) {
        return false;
      }

      const produto = this.produtos[0];
      const codigoProduto = this.normalizarCodigoBarras(produto.cod_barras);
      const codigoProdutoBalanca =
        codigoBarras.length === 13 && codigoBarras[0] === "2"
          ? codigoBarras.slice(0, 7)
          : null;

      if (
        codigoProduto !== codigoBarras &&
        codigoProduto !== codigoProdutoBalanca
      ) {
        return false;
      }

      await this.selecionarProduto(produto, true);
      return true;
    },
    adicionarItem() {
      if (!this.produtoSelecionado) {
        this.$toast.warning("Selecione um produto.");
        return;
      }

      const quantidade = this.toFloat(this.item.quantidade);
      const valorUnitario = this.toFloat(this.item.valor_unitario);
      const estoque = this.estoqueDisponivel(this.produtoSelecionado);
      const itemExistente = this.form.itens.find(
        (itemLinha) =>
          this.chaveEstoqueItem(itemLinha) ===
          this.chaveEstoqueProduto(this.produtoSelecionado)
      );
      const quantidadeFinal = itemExistente
        ? this.toFloat(itemExistente.quantidade) + quantidade
        : quantidade;

      if (
        this.produtoSelecionado.variavel &&
        !this.produtoSelecionado.combinacao
      ) {
        this.$toast.warning("Selecione uma variação para este produto.");
        return;
      }

      if (quantidade <= 0) {
        this.$toast.warning("Informe uma quantidade maior que zero.");
        return;
      }

      if (valorUnitario < 0) {
        this.$toast.warning("Informe um valor unitário válido.");
        return;
      }

      if (this.desconsiderarEstoqueCondicional) {
        // Flag ativa: nao valida estoque
      } else if (estoque !== null && quantidadeFinal > estoque) {
        this.$toast.warning("Quantidade informada é superior ao estoque.");
        return;
      }

      if (itemExistente) {
        itemExistente.quantidade = quantidadeFinal;
        itemExistente.estoque_disponivel = estoque;
        this.$toast.success(
          `Produto ${this.produtoSelecionado.descricao} inserido com sucesso.`
        );
        this.limparProdutoSelecionado();
        return;
      }

      const descricaoProdutoNovo = this.produtoSelecionado.descricao;

      this.form.itens.unshift({
        uid: `${this.produtoSelecionado.id}-${Date.now()}-${Math.random()}`,
        id_produto: this.produtoSelecionado.id,
        id_produto_lote:
          this.produtoSelecionado.lote_baixa?.id_produto_lote || null,
        combinacao: this.produtoSelecionado.combinacao || null,
        variavel: Boolean(this.produtoSelecionado.variavel),
        descricao: this.produtoSelecionado.descricao,
        codigo: this.produtoSelecionado.codigo,
        referencia: this.produtoSelecionado.referencia,
        estoque_disponivel: estoque,
        quantidade,
        valor_unitario: valorUnitario,
      });

      this.$toast.success(
        `Produto ${descricaoProdutoNovo} inserido com sucesso.`
      );
      this.limparProdutoSelecionado();
    },
    limparProdutoSelecionado() {
      this.produtoSelecionado = null;
      this.produtos = [];
      if (this.$refs.produtoSelect) {
        this.$refs.produtoSelect.search = "";
      }
      this.item = {
        quantidade: 1,
        valor_unitario: 0,
      };
      this.$nextTick(() => {
        this.$refs.produtoSelect?.$el?.querySelector("input")?.focus();
      });
    },
    removerItem(index) {
      this.form.itens.splice(index, 1);
    },
    validarQuantidadeLinha(item) {
      const quantidade = this.toFloat(item.quantidade);

      if (quantidade <= 0) {
        item.quantidade = 1;
        this.$toast.warning("Quantidade deve ser maior que zero.");
        return;
      }

      if (this.desconsiderarEstoqueCondicional) {
        // Flag ativa: nao ajusta quantidade
      } else if (
        item.estoque_disponivel !== null &&
        quantidade > this.toFloat(item.estoque_disponivel)
      ) {
        item.quantidade = this.toFloat(item.estoque_disponivel);
        this.$toast.warning("Quantidade ajustada ao estoque disponível.");
      }
    },
    validarFormulario() {
      if (
        !this.form.responsabilidade_vendedor &&
        !this.form.cliente?.id_pessoa
      ) {
        this.$toast.warning("Informe o cliente.");
        return false;
      }

      if (
        this.form.responsabilidade_vendedor &&
        !this.form.vendedor?.id_vendedor
      ) {
        this.$toast.warning("Informe o vendedor responsável.");
        return false;
      }

      if (!this.form.data_limite_retorno) {
        this.$toast.warning("Informe a data limite de retorno.");
        return false;
      }

      if (
        this.form.data_saida &&
        moment(this.form.data_limite_retorno).isBefore(
          moment(this.form.data_saida),
          "day"
        )
      ) {
        this.$toast.warning("Data limite deve ser igual ou posterior à saída.");
        return false;
      }

      if (this.form.itens.length === 0) {
        this.$toast.warning("Informe ao menos um produto.");
        return false;
      }

      const itemInvalido = this.form.itens.find(
        (item) =>
          this.toFloat(item.quantidade) <= 0 ||
          this.toFloat(item.valor_unitario) < 0
      );

      if (itemInvalido) {
        this.$toast.warning("Revise quantidades e valores dos itens.");
        return false;
      }

      return true;
    },
    montarPayload(confirmarSaida = false) {
      const payload = {
        responsabilidade_vendedor: this.form.responsabilidade_vendedor,
        cliente: this.form.responsabilidade_vendedor ? null : this.form.cliente,
        id_pessoa: this.form.responsabilidade_vendedor
          ? null
          : this.form.cliente?.id_pessoa,
        vendedor: this.form.vendedor,
        id_vendedor: this.form.vendedor?.id_vendedor || null,
        data_saida: this.form.data_saida || null,
        data_limite_retorno: this.form.data_limite_retorno,
        observacao: this.form.observacao || null,
        confirmar_saida: confirmarSaida,
        itens: this.form.itens.map((item) => ({
          id_produto: item.id_produto,
          id_produto_lote: item.id_produto_lote,
          combinacao: item.combinacao,
          quantidade: this.toFloat(item.quantidade),
          valor_unitario: this.toFloat(item.valor_unitario),
          descricao_produto_snapshot: item.descricao,
          codigo_produto_snapshot: item.codigo,
        })),
      };

      if (this.editando) {
        payload.id_condicional_mercadoria = this.idCondicionalMercadoria;
      }

      return payload;
    },
    async confirmarSalvarSaida() {
      swal({
        title: "Salvar e confirmar saída?",
        text: "O estoque ficará reservado para esta condicional.",
        icon: "info",
        buttons: {
          cancel: { text: "Cancelar", value: false, visible: true },
          confirm: { text: "Confirmar", value: true, visible: true },
        },
      }).then((confirmou) => {
        if (confirmou) {
          this.salvar(true);
        }
      });
    },
    async salvar(confirmarSaida) {
      if (!this.validarFormulario()) return;

      this.salvando = true;

      try {
        if (!(await this.validarEstoquesAntesSalvar(confirmarSaida))) {
          return;
        }

        if (this.editando) {
          await CondicionalMercadoriaData.update(
            this,
            this.montarPayload(confirmarSaida)
          );

          if (confirmarSaida) {
            this.$toast.success("Condicional atualizada e saída confirmada.");
          } else {
            this.$toast.success("Condicional atualizada com sucesso.");
          }
        } else {
          await CondicionalMercadoriaData.create(
            this,
            this.montarPayload(confirmarSaida)
          );

          if (confirmarSaida) {
            this.$toast.success("Saída confirmada e estoque reservado.");
          } else {
            this.$toast.success("Condicional criada com sucesso.");
          }
        }

        this.$router.push({ name: "condicionalMercadorias" });
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            error?.message ||
            "Erro ao salvar condicional."
        );
      } finally {
        this.salvando = false;
      }
    },
    async validarEstoquesAntesSalvar(confirmarSaida = false) {
      if (this.desconsiderarEstoqueCondicional) {
        return true;
      }

      const validarApenasDelta =
        this.editando &&
        !confirmarSaida &&
        Number(this.statusCondicional) !== 1;

      if (validarApenasDelta && !this.itensAlteradosParaEstoque()) {
        return true;
      }

      const quantidadesOriginais = validarApenasDelta
        ? this.quantidadesOriginaisPorChaveEstoque()
        : {};

      const grupos = this.form.itens.reduce((acc, item) => {
        const chave = this.chaveFisicaItemEstoque(item);

        if (!acc[chave]) {
          acc[chave] = {
            itemReferencia: item,
            quantidade: 0,
            quantidadeOriginal: quantidadesOriginais[chave] || 0,
            itens: [],
          };
        }

        acc[chave].quantidade += this.toFloat(item.quantidade);
        acc[chave].itens.push(item);

        return acc;
      }, {});

      for (const grupo of Object.values(grupos)) {
        const quantidadeValidar = validarApenasDelta
          ? Math.max(0, grupo.quantidade - grupo.quantidadeOriginal)
          : grupo.quantidade;

        if (quantidadeValidar <= 0.0001) {
          continue;
        }

        const estoque = await CondicionalMercadoriaData.estoqueDisponivel(
          this,
          {
            id_produto: grupo.itemReferencia.id_produto,
            id_produto_lote: grupo.itemReferencia.id_produto_lote || null,
            combinacao: grupo.itemReferencia.combinacao || null,
          }
        );
        const estoqueDisponivel = this.toFloat(estoque.estoque_disponivel);

        grupo.itens.forEach((item) => {
          item.estoque_disponivel = estoqueDisponivel;
        });

        if (quantidadeValidar > estoqueDisponivel + 0.0001) {
          this.$toast.error(
            `Estoque disponível insuficiente para ${
              grupo.itemReferencia.descricao
            }. Disponível: ${this.formatQuantidade(estoqueDisponivel)}.`
          );

          return false;
        }
      }

      return true;
    },
    quantidadesOriginaisPorChaveEstoque() {
      return this.itensOriginaisEstoque.reduce((acc, item) => {
        const chave = this.chaveFisicaItemEstoque(item);
        acc[chave] = (acc[chave] || 0) + this.toFloat(item.quantidade);

        return acc;
      }, {});
    },
    itensAlteradosParaEstoque() {
      return (
        JSON.stringify(this.assinaturaItensEstoque(this.form.itens)) !==
        JSON.stringify(this.itensOriginaisEstoque)
      );
    },
    assinaturaItensEstoque(itens) {
      return itens
        .map((item) => this.normalizarItemEstoque(item))
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    },
    normalizarItemEstoque(item) {
      return {
        id_produto: Number(item.id_produto || 0),
        id_produto_lote: item.id_produto_lote
          ? Number(item.id_produto_lote)
          : null,
        combinacao: this.normalizarCombinacaoEstoque(item.combinacao || null),
        quantidade: Number(this.toFloat(item.quantidade).toFixed(6)),
      };
    },
    normalizarCombinacaoEstoque(combinacao) {
      if (!combinacao) {
        return null;
      }

      if (typeof combinacao === "string") {
        try {
          combinacao = JSON.parse(combinacao);
        } catch (error) {
          return null;
        }
      }

      if (!combinacao || typeof combinacao !== "object") {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(combinacao, "combinacao")) {
        return this.normalizarCombinacaoEstoque(combinacao.combinacao);
      }

      const normalizada = Object.keys(combinacao)
        .filter((key) => key !== "quantidade")
        .sort()
        .reduce((acc, key) => {
          const value = combinacao[key];
          acc[key] =
            value && typeof value === "object"
              ? this.normalizarCombinacaoEstoque(value)
              : value;
          return acc;
        }, {});

      return Object.keys(normalizada).length ? normalizada : null;
    },
    chaveFisicaItemEstoque(item) {
      const normalizado = this.normalizarItemEstoque(item);

      return JSON.stringify({
        id_produto: normalizado.id_produto,
        id_produto_lote: normalizado.id_produto_lote,
        combinacao: normalizado.combinacao,
      });
    },
    chaveEstoqueItem(item) {
      return JSON.stringify({
        id_produto: item.id_produto,
        id_produto_lote: item.id_produto_lote || null,
        combinacao: item.combinacao || null,
      });
    },
    chaveEstoqueProduto(produto) {
      return JSON.stringify({
        id_produto: produto.id,
        id_produto_lote: produto.lote_baixa?.id_produto_lote || null,
        combinacao: produto.combinacao || null,
      });
    },
    normalizarCodigoBarras(codigo) {
      return String(codigo || "").trim();
    },
    estoqueDisponivel(produto) {
      if (!produto) return null;

      const estoque =
        produto.estoque_disponivel ??
        produto.quantidade_original_combinacao ??
        produto.combinacao?.quantidade ??
        produto.quantidade_original ??
        produto.quantidade_original_produto ??
        produto.estoque ??
        produto.saldo_estoque ??
        produto.quantidade;

      if (estoque === undefined || estoque === null || estoque === "") {
        return null;
      }

      return this.toFloat(estoque);
    },
    combinacaoLabel(item) {
      const combinacao = item?.combinacao;

      if (!combinacao) return "";

      return Object.keys(combinacao)
        .filter((key) => key !== "quantidade")
        .map((key) => {
          const value = combinacao[key];
          const label = value && typeof value === "object" ? value.nome : value;
          return label ? `${key}: ${label}` : "";
        })
        .filter(Boolean)
        .join(" / ");
    },
    totalItem(item) {
      return this.toFloat(item.quantidade) * this.toFloat(item.valor_unitario);
    },
    toFloat(value) {
      if (value === undefined || value === null || value === "") return 0;
      if (typeof value === "string") {
        if (value.includes(",")) {
          return Number(value.replace(/\./g, "").replace(",", "."));
        }

        return Number(value);
      }

      return Number(value);
    },
    formatQuantidade(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "-";
      }

      return this.toFloat(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    formatValor(value) {
      return this.toFloat(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.condicional-create-page .card {
  border-radius: 6px;
}

.condicional-number-input {
  min-width: 110px;
}

.condicional-responsabilidade-flag {
  align-items: center;
  display: flex;
  min-height: 43px;
}
</style>
